import { isEmpty, last, omit } from "lodash"
import React, { useEffect, useState } from "react"
import { Button, Col, Form, Modal, Row } from "react-bootstrap"
import { XSquareFill } from "react-bootstrap-icons"
import { useTranslation } from "react-i18next"
import { useAppSelector } from "../../app/hooks"
import { DHL, FEDEX, SHOPINI_ECONOMY, UPS } from "../../constants/constants"
import { RootState } from "../../store"
import { countryNamesInEnglish } from "../../utils/commonFunctions"

interface SelectedParcel {
    uuid: string
    weight: string
}

const ConsolidateModal = ({
    show,
    setShow,
    consolidateBox,
    country
}: {
    show: string
    setShow: Function
    consolidateBox: Function
    country?: string
}) => {
    const { t } = useTranslation()
    const [childParcels, setChildParcels] = useState([] as Record<string, any>[])
    const [parcelsInBoxes, setParcelsInBoxes] = useState({} as Record<string, any>)
    const [selectedParcels, setSelectedParcels] = useState([] as SelectedParcel[])
    const [counter, setCounter] = useState(1)
    const { consolBox } = useAppSelector((state: RootState) => state.consolidations)

    useEffect(() => {
        setSelectedParcels([])
        setParcelsInBoxes({})
        setCounter(1)
    }, [])

    useEffect(() => {
        setChildParcels(consolBox?.Children)
    }, [consolBox?.uuid])

    const handleRemoveParcel = async (key: string, parcel: SelectedParcel) => {
        if (parcelsInBoxes[key] && parcelsInBoxes[key]?.length > 1) {
            setParcelsInBoxes({
                ...parcelsInBoxes,
                [key]: parcelsInBoxes[key]?.filter((p: SelectedParcel) => p.uuid !== parcel?.uuid)
            })
        } else {
            //document.getElementById(`${consolBox?.uuid}-${Number(key) - 1}`)?.remove()
            let parcelsInBoxesWithNewKeys = {} as Record<string, any>
            await setParcelsInBoxes({
                ...omit(parcelsInBoxes, key)
            })
            await Object.keys(omit(parcelsInBoxes, key))?.forEach((k: string) => {
                if (Number(k) > Number(key)) {
                    parcelsInBoxesWithNewKeys[`${Number(k) - 1}`] = parcelsInBoxes[k]
                } else {
                    const objectWithoutKey = omit(parcelsInBoxes, key) as Record<string, any>
                    if (!isEmpty(objectWithoutKey)) {
                        parcelsInBoxesWithNewKeys[k] = objectWithoutKey[k]
                    } else {
                        parcelsInBoxesWithNewKeys = {}
                    }
                }
            })
            await setParcelsInBoxes({
                ...parcelsInBoxesWithNewKeys
            })
            setCounter(counter - 1)
        }

        setChildParcels([
            ...childParcels,
            consolBox?.Children?.find((p: Record<string, any>) => p?.uuid === parcel?.uuid)
        ])
    }

    const handleSubmit = (partial: boolean) => {
        let tempParcelsInBoxes = {} as Record<string, any>

        Object.keys(parcelsInBoxes)?.forEach(
            (k) => (tempParcelsInBoxes[k] = parcelsInBoxes[k].map((p: SelectedParcel) => p.uuid))
        )

        if (selectedParcels?.length > 0) {
            const addedKey =
                (Number(
                    last(Object.keys(parcelsInBoxes).sort((a: string, b: string) => Number(a) - Number(b)))
                ) || 0) + 1

            tempParcelsInBoxes = {
                ...tempParcelsInBoxes,
                [`${addedKey}`]: selectedParcels.map((p: SelectedParcel) => p.uuid)
            }
        }
        //selectedParcels?.map((parcel: any) => document.getElementById(parcel?.uuid)?.remove())
        setSelectedParcels([])

        consolidateBox({
            parcels_in_boxes: tempParcelsInBoxes,
            suite_id: consolBox?.suite,
            consolidation_box_uuid: partial ? undefined : consolBox?.uuid
        })
    }

    const exportParcelsToExcel = () => {
        let dataToExport = [
            "Parcel ID,Parcel UUID,Weight,Items,Amount,Supplier",
            ...childParcels?.map((cp) => {
                return `\n${cp?.id},${cp?.uuid},${cp?.weight_value} ${cp?.weight_unit},${cp?.ParcelItems?.length || 0}, ${cp?.amount}, ${cp?.Website?.name || ""}`
            })
        ] as unknown as BlobPart
        const outputFilename = `${Date.now()}.csv`
        const aElement = document.createElement("a")
        aElement.setAttribute("download", outputFilename)
        const href = URL.createObjectURL(new Blob([dataToExport]))
        aElement.href = href
        aElement.setAttribute("target", "_blank")
        aElement.click()
        URL.revokeObjectURL(href)
    }

    return (
        <Modal
            size="xl"
            show={show === "consolidate"}
            onHide={() => {
                setSelectedParcels([])
                setParcelsInBoxes({})
                setChildParcels(consolBox?.Children)
                setCounter(1)
                setShow("")
            }}
            backdrop="static"
            keyboard={false}>
            <Modal.Header closeButton>
                <Modal.Title className="text-uppercase">
                    {consolBox?.shippingService === SHOPINI_ECONOMY && country && country !== ""
                        ? `${t("Master console")} - ${countryNamesInEnglish.of(country || "")}`
                        : consolBox?.uuid}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="consolidation-modal bg-light">
                <Row className="d-flex justify-content-around">
                    <Col
                        lg={
                            [DHL, FEDEX, SHOPINI_ECONOMY, UPS].includes(consolBox?.shippingService)
                                ? !(country && country !== "")
                                    ? 8
                                    : 6
                                : 12
                        }
                        className="scrollable">
                        <Form>
                            <Form.Group>
                                {!(country && country !== "") && (
                                    <Row className="bg-transparent border custom-thead py-2 mx-2 rounded text-uppercase">
                                        <Col xs={1}>
                                            <Form.Check
                                                type="checkbox"
                                                onChange={(e: any) => {
                                                    if (e.target.checked) {
                                                        setSelectedParcels([
                                                            ...childParcels?.map((cp) => {
                                                                return {
                                                                    uuid: cp?.uuid,
                                                                    weight: `${cp?.weight_value} ${cp?.weight_unit}`
                                                                }
                                                            })
                                                        ])
                                                    } else {
                                                        setSelectedParcels([])
                                                    }
                                                }}
                                            />
                                        </Col>
                                        <Col xs={3}>{t("Parcel ID")}</Col>
                                        <Col xs={1}>{t("Items")}</Col>
                                        <Col xs={3}>{t("Supplier")}</Col>
                                        <Col xs={2}>{t("Value")}</Col>
                                        <Col xs={2}>{t("CH. Weight")}</Col>
                                    </Row>
                                )}
                                {childParcels?.map((childParcel: Record<string, any>, index: number) => (
                                    <Row
                                        key={childParcel.uuid}
                                        id={childParcel.uuid}
                                        className={`${[DHL, FEDEX, SHOPINI_ECONOMY, UPS].includes(consolBox?.shippingService) ? "" : "container"} mx-2 ${country && country !== "" ? (index < childParcels?.length - 1 ? "border-bottom py-3" : "") : "py-2 rounded bg-white my-2"}`}>
                                        <Col lg={country && country !== "" ? 6 : 4}>
                                            <Form.Check
                                                type="checkbox"
                                                checked={
                                                    !!selectedParcels?.find(
                                                        (sp) => sp?.uuid === childParcel.uuid
                                                    )
                                                }
                                                label={childParcel.uuid}
                                                value={childParcel.uuid}
                                                className={country && country !== "" ? "" : "primary-text"}
                                                onChange={(e: any) => {
                                                    if (e.target.checked) {
                                                        setSelectedParcels([
                                                            ...selectedParcels,
                                                            {
                                                                uuid: childParcel?.uuid,
                                                                weight: `${childParcel?.weight_value} ${childParcel?.weight_unit}`
                                                            }
                                                        ])
                                                    } else {
                                                        setSelectedParcels(
                                                            selectedParcels?.filter(
                                                                (selectedParcel: SelectedParcel) =>
                                                                    selectedParcel?.uuid !== childParcel?.uuid
                                                            )
                                                        )
                                                    }
                                                }}
                                            />
                                        </Col>
                                        {!(country && country !== "") && (
                                            <>
                                                <Col lg={1} className="">
                                                    <span className="">{childParcel?.ParcelItems?.length}</span>
                                                </Col>
                                                <Col lg={3} className="">
                                                    <span className="">{`${childParcel?.Website?.name?.substring(0, 35)}...`}</span>
                                                </Col>
                                                <Col lg={2} className="">
                                                    <span className="">{`$ ${childParcel?.amount}`}</span>
                                                </Col>
                                            </>
                                        )}
                                        <Col
                                            lg={country && country !== "" ? 6 : 2}
                                            className={country && country !== "" ? "text-end" : ""}>
                                            <span className="">{`${childParcel?.weight_value} ${childParcel?.weight_unit}`}</span>
                                        </Col>
                                    </Row>
                                ))}
                            </Form.Group>
                        </Form>
                    </Col>
                    <Col
                        lg={country && country !== "" ? 5 : 4}
                        className={
                            [DHL, FEDEX, SHOPINI_ECONOMY, UPS].includes(consolBox?.shippingService)
                                ? "scrollable"
                                : "d-none"
                        }>
                        {Object.keys(parcelsInBoxes).map((key: string, index: number) => (
                            <Row key={`${consolBox?.uuid}-${Number(key) - 1}`} className="mb-3">
                                <Col lg={12}>
                                    <span className={"pb-3"} id={`${consolBox?.uuid}-${Number(key) - 1}`}>
                                        {`Box ${Number(key)}`}
                                        {/*{consolBox?.uuid}{index === 0 ? "" : "-" + (Number(key) - 1)}*/}
                                    </span>
                                    {parcelsInBoxes[key]?.map((parcel: SelectedParcel) => (
                                        <div className="px-2 py-2 dark-grey-bg rounded my-3 d-flex justify-content-between">
                                            <span>{parcel?.uuid}</span>
                                            <span>
                                                {parcel?.weight}
                                                <XSquareFill
                                                    className="text-danger d-inline mx-1"
                                                    role="button"
                                                    onClick={() => handleRemoveParcel(key, parcel)}
                                                />
                                            </span>
                                        </div>
                                    ))}
                                </Col>
                            </Row>
                        ))}
                    </Col>
                </Row>
            </Modal.Body>
            <Modal.Footer>
                <Button onClick={exportParcelsToExcel} className={"primary-bg"}>
                    {t("Export")}
                </Button>
                <Button
                    variant="secondary"
                    disabled={
                        childParcels?.length === selectedParcels?.length ||
                        selectedParcels?.length === 0 ||
                        childParcels?.length === 0
                    }
                    className={
                        [DHL, FEDEX, SHOPINI_ECONOMY, UPS].includes(consolBox?.shippingService) ? "" : "d-none"
                    }
                    onClick={() => {
                        setParcelsInBoxes({
                            ...parcelsInBoxes,
                            [counter.toString()]: [...selectedParcels]
                        })
                        setChildParcels([
                            ...childParcels?.filter(
                                (parcel: Record<string, any>) =>
                                    !selectedParcels.map((sp: SelectedParcel) => sp.uuid)?.includes(parcel.uuid)
                            )
                        ])
                        //selectedParcels?.map((parcel: any) => document.getElementById(parcel?.uuid)?.remove())
                        setCounter(counter + 1)
                        setSelectedParcels([])
                    }}>
                    {t("Add more boxes")}
                </Button>
                {(consolBox?.shippingService !== SHOPINI_ECONOMY || !country || country === "") && (
                    <Button
                        variant="dark"
                        disabled={
                            (isEmpty(parcelsInBoxes) && selectedParcels?.length === 0) ||
                            childParcels?.length === selectedParcels?.length ||
                            childParcels?.length === 0
                        }
                        onClick={() => handleSubmit(true)}>
                        {t("Partial Consolidation")}
                    </Button>
                )}
                <Button
                    disabled={
                        (childParcels?.length > selectedParcels?.length &&
                            (consolBox?.shippingService !== SHOPINI_ECONOMY || !country || country === "")) ||
                        (selectedParcels?.length === 0 && isEmpty(parcelsInBoxes))
                    }
                    onClick={() => handleSubmit(false)}
                    className={"primary-bg"}>
                    {t("Consolidate")}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

export default ConsolidateModal
